import {
    DollarCircleOutlined,
    RetweetOutlined,
    UserSwitchOutlined,
} from "@ant-design/icons";
import { MenuProps } from "antd";
import { FC, useCallback, useMemo } from "react";
import {
    AnyTask,
    AssignmentType,
    Market,
    TaskRoute,
    UserRole,
} from "tm-shared/types";

import FlexContainer from "@/components/FlexContainer";
import { MarketActionsBar } from "@/components/ItemListView/MarketActionsBar";
import { ASSIGNMENT_VOCABULARY } from "@/constants/ui/assignment";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useCreateTask } from "@/hooks/useCreateTask";
import { useQueueInfo } from "@/hooks/useQueueInfo";
import { MarketTasks } from "@/pages/WorkPlan/components/MarketTasks";
import { addModalToQueue } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";

import { mapMarketForCopy, sortStartVisitFirstTasks } from "./MarketBody.utils";
import { StyledAdditionalSaleButton, StyledMenuButton } from "./Styled";

const rolesAllowedToWorkWithSales: UserRole[] = [UserRole.Sv, UserRole.Ta];

type MenuItemWithClose = NonNullable<MenuProps["items"]>[number] & {
    closeDropdown?: boolean;
};

interface MarketBodyProps {
    item: Market;
    onOpenTasksInfoModal: (params: { task: AnyTask; market: Market }) => void;
}

export const MarketBody: FC<MarketBodyProps> = ({
    item: market,
    onOpenTasksInfoModal,
}) => {
    const { role } = useAppSelector((s) => s.user);
    const { tasks } = useAppSelector((s) => s.work);
    const { offlineMode } = useAppSelector((s) => s.offline);

    const dispatch = useAppDispatch();

    const { isRunning: isQueueRunning } = useQueueInfo();

    const marketId = market.id;

    const marketTasks = useMemo(
        () =>
            tasks[market.id]
                ? sortStartVisitFirstTasks(Object.values(tasks[market.id]))
                : [],
        [tasks, market.id],
    );

    const {
        createAdditionalSale,
        createTakeDebtTask,
        createCustomTask,
        createRejectTask,
        creating: creatingTask,
    } = useCreateTask({ marketId, marketTasks });

    const incompleteRoutes = useMemo(() => {
        const set = new Set<TaskRoute>();
        for (const t of marketTasks) {
            if (!t.completed) set.add(t.context.route);
        }
        return set;
    }, [marketTasks]);

    const canCreateTask = useCallback(
        (route: TaskRoute, options?: { ignoreCreatingTask?: boolean }) => {
            const hasUncompletedTask = incompleteRoutes.has(route);
            const hasIncompleteStartVisit = incompleteRoutes.has(
                TaskRoute.StartVisit,
            );
            const hasIncompleteGeoLocation = incompleteRoutes.has(
                TaskRoute.SetGeoLocation,
            );

            const creatingAnyTask = !!creatingTask;

            const blockedByStartVisit =
                hasIncompleteStartVisit && route !== TaskRoute.StartVisit;

            const blockedByCreatingTask =
                !options?.ignoreCreatingTask && creatingAnyTask;

            return (
                !isQueueRunning &&
                !hasUncompletedTask &&
                !blockedByCreatingTask &&
                !blockedByStartVisit &&
                !hasIncompleteGeoLocation
            );
        },
        [creatingTask, incompleteRoutes, isQueueRunning],
    );

    const createCustomTaskForAgent = useCallback(async () => {
        dispatch(
            addModalToQueue({
                type: ModalType.CreateCustomTask,
                id: ModalType.CreateCustomTask,
                payload: {
                    market,
                    images: [],
                },
            }),
        );
    }, [dispatch, market]);

    const hasSalesRole = useMemo(
        () => rolesAllowedToWorkWithSales.includes(role),
        [role],
    );

    const canCreateAdditionalSale = useMemo(() => {
        const canCreate = canCreateTask(TaskRoute.SaleAdditional, {
            ignoreCreatingTask: true,
        });

        return canCreate && hasSalesRole;
    }, [canCreateTask, hasSalesRole]);

    const getDropdownMenu = useCallback((): MenuProps => {
        const menuProps: { items: MenuItemWithClose[] } = {
            items: [],
        };

        if (hasSalesRole) {
            menuProps.items.push({
                key: "1",
                label: (
                    <StyledMenuButton
                        type="text"
                        icon={<RetweetOutlined />}
                        loading={creatingTask === TaskRoute.RejectProducts}
                    >
                        Повернення товарів
                    </StyledMenuButton>
                ),
                onClick: createRejectTask,
                disabled: !canCreateTask(TaskRoute.RejectProducts),
            });

            if (market.hasDebt) {
                menuProps.items.push({
                    key: "2",
                    label: (
                        <StyledMenuButton
                            type="text"
                            icon={<DollarCircleOutlined />}
                            loading={creatingTask === TaskRoute.TakeDebt}
                        >
                            Забрати борг
                        </StyledMenuButton>
                    ),
                    onClick: createTakeDebtTask,
                    disabled: !canCreateTask(TaskRoute.TakeDebt),
                });
            }
        }

        const PricesDistributionIcon =
            ASSIGNMENT_VOCABULARY[AssignmentType.PricesDistribution].icon;

        menuProps.items.push({
            key: "3",
            label: (
                <StyledMenuButton
                    type="text"
                    icon={<PricesDistributionIcon />}
                    loading={creatingTask === TaskRoute.CustomTask}
                >
                    {
                        ASSIGNMENT_VOCABULARY[AssignmentType.PricesDistribution]
                            .label
                    }
                </StyledMenuButton>
            ),
            onClick: createCustomTask,
            disabled: !canCreateTask(TaskRoute.CustomTask),
        });

        if (isManagerRole(role)) {
            menuProps.items.push({
                key: "4",
                label: (
                    <StyledMenuButton type="text" icon={<UserSwitchOutlined />}>
                        Створення доручення
                    </StyledMenuButton>
                ),
                onClick: createCustomTaskForAgent,
                disabled: offlineMode,
                closeDropdown: true,
            });
        }

        return menuProps;
    }, [
        hasSalesRole,
        creatingTask,
        createCustomTask,
        canCreateTask,
        role,
        createRejectTask,
        market.hasDebt,
        createTakeDebtTask,
        createCustomTaskForAgent,
        offlineMode,
    ]);

    const menu = marketTasks?.length > 0 ? getDropdownMenu() : undefined;

    return (
        <FlexContainer
            flexDirection="column"
            justifyContent="center"
            alignItems="flex-start"
            gap="16px"
            width="100%"
        >
            <MarketActionsBar
                marketId={market.id}
                marketName={market.name}
                objToCopy={mapMarketForCopy(market)}
                geo={market.geo}
                address={market.address.name}
                menu={menu}
            />

            <MarketTasks
                tasks={marketTasks}
                market={market}
                incompleteRoutes={incompleteRoutes}
                onOpenTasksInfoModal={onOpenTasksInfoModal}
            />

            {canCreateAdditionalSale && (
                <StyledAdditionalSaleButton
                    loading={creatingTask === TaskRoute.SaleAdditional}
                    type="text"
                    onClick={createAdditionalSale}
                    disabled={!!creatingTask}
                >
                    Додатковий продаж
                </StyledAdditionalSaleButton>
            )}
        </FlexContainer>
    );
};
